import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Crown, LogOut, TrendingUp, ShoppingBag, Package, Tag, BarChart2, Warehouse, Settings, Inbox, MoreHorizontal, UserCog, Mail, User as UserIcon } from 'lucide-react';
import { User } from '../types/alert.types';

interface SidebarProps {
  user: User | null;
  currentPage?: string;
  onLogout: () => void;
  onPageChange?: (page: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  user,
  currentPage = 'alerts',
  onLogout,
  onPageChange
}) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleNavigation = (page: string) => {
    if (onPageChange) onPageChange(page);
    if (page === 'alerts') {
      navigate('/app');
    } else {
      navigate(`/app?page=${page}`);
    }
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col fixed h-full">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h1
            className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleNavigation('alerts')}
          >
            SmartResell
          </h1>
          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-lg flex-shrink-0">{user?.country || 'FR'}</span>
        </div>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto space-y-1">

        {/* ── Section Scraping des prix ── */}
        <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
          <Bell size={13} />
          <span>Scraping des prix</span>
        </div>
        <div className="pl-2 flex flex-col gap-0.5">
          <button
            onClick={() => handleNavigation('alerts')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'alerts' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Bell size={17} /><span>Alertes</span>
          </button>
          <button
            onClick={() => handleNavigation('optimize')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'optimize' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <TrendingUp size={17} /><span>Historique</span>
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 my-2" />

        {/* ── Section Vinted CRM ── */}
        <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
          <ShoppingBag size={13} />
          <span>Vinted CRM</span>
        </div>
        <div className="pl-2 flex flex-col gap-0.5">
          <button
            onClick={() => handleNavigation('crm-achats')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'crm-achats' ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Package size={17} /><span>Achats</span>
          </button>
          <button
            onClick={() => handleNavigation('crm-reception')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'crm-reception' ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Inbox size={17} /><span>Réception</span>
          </button>
          <button
            onClick={() => handleNavigation('crm-stock')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'crm-stock' ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Warehouse size={17} /><span>Stock</span>
          </button>
          <button
            onClick={() => handleNavigation('crm-ventes')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'crm-ventes' ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Tag size={17} /><span>Ventes</span>
          </button>
          <button
            onClick={() => handleNavigation('crm-comptabilite')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'crm-comptabilite' ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <BarChart2 size={17} /><span>Comptabilité</span>
          </button>
          <button
            onClick={() => handleNavigation('crm-configuration')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'crm-configuration' ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Settings size={17} /><span>Configuration</span>
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 my-2" />

        {/* ── Mon espace ── */}
        <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
          <UserIcon size={13} />
          <span>Mon espace</span>
        </div>
        <div className="pl-2 flex flex-col gap-0.5">
          <button
            onClick={() => handleNavigation('subscription')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'subscription' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Crown size={17} /><span>Abonnement</span>
          </button>
          <button
            onClick={() => handleNavigation('contact')}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
              currentPage === 'contact' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Mail size={17} /><span>Contact</span>
          </button>
        </div>

      </nav>

      <div className="p-4 border-t border-gray-200 relative" ref={menuRef}>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.full_name || 'Utilisateur'}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
          </div>
          <button
            onClick={() => setMenuOpen(prev => !prev)}
            className="ml-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            title="Options"
          >
            <MoreHorizontal size={18} />
          </button>
        </div>

        {menuOpen && (
          <div className="absolute bottom-16 left-4 right-4 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
            <button
              onClick={() => { setMenuOpen(false); if (onPageChange) onPageChange('account-settings'); navigate('/app?page=account-settings'); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all"
            >
              <UserCog size={16} className="text-gray-400" />
              Paramètres du compte
            </button>
            <div className="h-px bg-gray-100" />
            <button
              onClick={() => { setMenuOpen(false); onLogout(); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-all"
            >
              <LogOut size={16} />
              Se déconnecter
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
